import {css} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {nameTemplatePartChipStyles} from 'name-templates/NameTemplateConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {assertNever} from 'util/Types';

import {InputFileNamePart, InputFileNamePartType} from './AutomationInputFileConfigConstants';

function formatPart(nameTemplatePart: InputFileNamePart, schemaFields: Array<SchemaField>): React.ReactNode {
  switch (nameTemplatePart.type) {
    case InputFileNamePartType.FIELD: {
      const field = _.findWhere(schemaFields, {apiId: nameTemplatePart.fieldId});
      if (field) {
        return <span className={css(nameTemplatePartChipStyles.fieldName)}>{field.name}</span>;
      } else {
        // This shouldn't really happen (due to schema validation) but if it does, no reason to error out
        // just display a temp value until React can reload the new schema.
        return <span className={css(nameTemplatePartChipStyles.fieldName)}>{nameTemplatePart.fieldId}</span>;
      }
    }
    case InputFileNamePartType.TEXT:
      return <span className={css(nameTemplatePartChipStyles.text)}>{nameTemplatePart.text}</span>;
    case InputFileNamePartType.CREATED_AT_DATE:
      return 'Creation date';
    case InputFileNamePartType.CREATED_AT_TIME:
      return 'Creation time';
    case InputFileNamePartType.DISPLAY_NAME:
      return 'Display name';
    case InputFileNamePartType.RUN_ID:
      return 'Run ID';
    case InputFileNamePartType.RUN_API_ID:
      return 'Run API ID';
    case InputFileNamePartType.RUN_SCHEMA_NAME:
      return 'Run schema name';
    case InputFileNamePartType.SOURCE_FILE_NAME:
      return 'Source file name';
    default:
      assertAndContinue(false, `Unexpected part type: ${assertNever(nameTemplatePart).type}`);
      return '(Unknown)';
  }
}

export interface InputFileNameTemplateProps {
  draggable?: boolean;
  handleDragStart?: (index: number) => void;
  handleDrop?: (index: number) => void;
  nameTemplateParts: Array<InputFileNamePart>;
  schemaFields: Array<SchemaField>;
  // If specified, show an x on each chip and call it when the x is clicked.
  onRemovePart?: (index: number) => void;
}

export default function InputFileNameTemplate(props: InputFileNameTemplateProps): JSX.Element {
  const {onRemovePart} = props;
  return (
    <span>
      {props.nameTemplateParts.map((nameTemplatePart, i) => (
        <Chip
          key={i}
          className={css(props.draggable && nameTemplatePartChipStyles.dragCursor)}
          draggable={props.draggable}
          onDragStart={(e) => {
            if (props.handleDragStart) {
              props.handleDragStart(i);
            }
          }}
          onDragOver={(e) => {
            // Need to preventDefault here otherwise the event doesn't get propagated to onDrop
            e.preventDefault();
          }}
          onDrop={(e) => {
            if (props.handleDrop) {
              props.handleDrop(i);
            }
          }}
          size={ChipSize.MINI}
          text={formatPart(nameTemplatePart, props.schemaFields)}
          onRemove={
            onRemovePart &&
            (() => {
              onRemovePart(i);
            })
          }
        />
      ))}
    </span>
  );
}
